// Super-admin Sports Catalogue management.
// Toggle a sport's system-wide `enabled` flag. Only enabled sports can be
// offered by schools (see the Sports section on the school-detail page) and
// appear when creating teams, events, and players.
import { useEffect, useState } from "react";
import type { SportPublic } from "@dugads/shared";
import { useAuth } from "@/lib/auth-context";
import * as api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trophy } from "lucide-react";

export default function AdminSportsPage() {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.roles.some((r) => r.role_type === "super_admin");

  const [sports, setSports] = useState<SportPublic[] | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api
      .getAllSports()
      .then((res) => setSports(res.sports))
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load sports"));
  }, [isSuperAdmin]);

  async function toggle(sport: SportPublic) {
    const next = !sport.enabled;
    setSavingId(sport.id);
    setError(null);
    try {
      const res = await api.setSportEnabled(sport.id, next);
      setSports((prev) => (prev ? prev.map((s) => (s.id === sport.id ? res.sport : s)) : prev));
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to save");
    } finally {
      setSavingId(null);
    }
  }

  if (!isSuperAdmin) {
    return (
      <div
        className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive"
        data-page="admin-sports"
        data-state="forbidden"
      >
        Only a super admin can manage the sports catalogue.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-page="admin-sports" data-state={sports === null ? "loading" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Trophy className="h-6 w-6" />
          Sports Catalogue
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Enable a sport to make it available platform-wide. Only enabled sports can be offered by
          schools and selected when creating teams, events, and players. Disabling a sport hides it
          from new selections but does not delete existing teams or events.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sports</CardTitle>
        </CardHeader>
        <CardContent>
          {error && <p className="mb-3 text-sm text-destructive">{error}</p>}
          {sports === null ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : sports.length === 0 ? (
            <p className="text-sm text-muted-foreground">No sports in the catalogue.</p>
          ) : (
            <div className="divide-y divide-border" data-field="sports">
              {sports.map((sport) => (
                <div
                  key={sport.id}
                  className="flex items-center justify-between gap-4 py-3"
                  data-sport={sport.id}
                  data-state={sport.enabled ? "on" : "off"}
                >
                  <div>
                    <p className="text-sm font-medium">{sport.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {sport.id}
                      {sport.court_type_name ? ` · ${sport.court_type_name}` : ""}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant={sport.enabled ? "default" : "outline"}
                    onClick={() => toggle(sport)}
                    disabled={savingId === sport.id}
                    data-action="toggle-sport"
                  >
                    {savingId === sport.id ? "Saving..." : sport.enabled ? "Enabled" : "Disabled"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
